import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Barcode, Package, CheckCircle, AlertTriangle, Truck, List, X, Filter, SkipForward, FileText, RefreshCw, Layers, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PickingPage() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [pickingList, setPickingList] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pickedLocation, setPickedLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showCompletedDialog, setShowCompletedDialog] = useState(false);
  const [showFullList, setShowFullList] = useState(false);
  const [transportFilter, setTransportFilter] = useState("all"); // all, Bring, DSV
  const [selectedItemId, setSelectedItemId] = useState(null); // For manual item selection from list
  const [statusFilter, setStatusFilter] = useState("all"); // all, pending, picked, missing
  const [showFilteredList, setShowFilteredList] = useState(false); // Show filtered list dialog
  const [currentStock, setCurrentStock] = useState(null); // Track current stock for item
  const [showDetailsDialog, setShowDetailsDialog] = useState(false); // Show details dialog for admin
  const [showProductSummary, setShowProductSummary] = useState(false); // Show product summary dialog
  const [lockedOrderNumber, setLockedOrderNumber] = useState(null); // Stay within current order until it's fully picked
  const [undoTarget, setUndoTarget] = useState(null); // Item being undone via the Ångra plock dialog
  const [undoLocation, setUndoLocation] = useState(""); // Location chosen for the undo
  const [undoRebook, setUndoRebook] = useState(true); // Whether to recreate auto-cancelled bookings

  // Get user from AuthContext
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchPickingList();
  }, [listId]);

  // Helper to get best sorted location for an item
  const getBestLocation = (item) => {
    if (!item) return '';
    if (!item.alla_lagerplatser) return item.lagerplats || '';
    
    const locs = item.alla_lagerplatser.split(',').map(l => l.trim()).filter(l => l && l !== '0');
    if (locs.length === 0) return item.lagerplats || '';
    
    // Sort: indoor first, outdoor (Ute) last
    const sortedLocs = locs.sort((a, b) => {
      const aIsUte = a.toLowerCase().startsWith('ute');
      const bIsUte = b.toLowerCase().startsWith('ute');
      if (aIsUte && !bIsUte) return 1;
      if (!aIsUte && bIsUte) return -1;
      return a.localeCompare(b, 'sv', { numeric: true });
    });
    
    return sortedLocs[0] || item.lagerplats || '';
  };

  // Sort items by ordernummer then artikelnummer (matches the picking list view sort order)
  const sortByOrderAndArt = (a, b) => {
    const orderA = a.ordernummer || '';
    const orderB = b.ordernummer || '';
    if (orderA !== orderB) return orderA.localeCompare(orderB);
    return (a.artikelnummer || '').localeCompare(b.artikelnummer || '');
  };

  // Given a fresh picking list, decide if we should advance the order lock.
  // Stay on the same order while it still has pending/partial items; otherwise
  // jump to the next order alphabetically (wraps to the first if at the end).
  // currentLock is passed explicitly to avoid stale-state issues right after a setState.
  const advanceOrderLockIfDone = (listData, currentLock) => {
    if (!listData) return;
    const pending = listData.items.filter(i => i.status === 'pending' || i.status === 'partial');
    if (pending.length === 0) return; // completion is handled by status === 'completed'
    const sorted = [...pending].sort(sortByOrderAndArt);

    if (currentLock && sorted.some(i => i.ordernummer === currentLock)) return;

    let nextOrder = null;
    if (currentLock) {
      const next = sorted.find(i => (i.ordernummer || '').localeCompare(currentLock) > 0);
      if (next) nextOrder = next.ordernummer || null;
    }
    if (nextOrder === null) nextOrder = sorted[0].ordernummer || null;

    if (nextOrder !== currentLock) {
      setLockedOrderNumber(nextOrder);
      if (currentLock && nextOrder) {
        toast.success(`Order ${currentLock} klar – fortsätter med ${nextOrder}`, { duration: 4000 });
      }
    }
  };

  useEffect(() => {
    if (pickingList) {
      // Find next pending item
      const nextPending = pickingList.items.findIndex(item => item.status === 'pending');
      if (nextPending !== -1) {
        setCurrentIndex(nextPending);
      }
    }
  }, [pickingList]);

  // Update picked location and fetch stock when current item changes
  useEffect(() => {
    if (!pickingList) return;
    
    // Calculate current item from state - include both pending and partial items
    const allPending = pickingList.items.filter(item => item.status === 'pending' || item.status === 'partial');
    const filtered = transportFilter === 'all' 
      ? allPending 
      : allPending.filter(item => item.transportor === transportFilter);
    const sortedFiltered = [...filtered].sort(sortByOrderAndArt);

    const selected = selectedItemId ? filtered.find(item => item.id === selectedItemId) : null;
    const lockedItem = lockedOrderNumber
      ? sortedFiltered.find(item => item.ordernummer === lockedOrderNumber)
      : null;
    const item = selected || lockedItem || sortedFiltered[0];
    
    if (item) {
      const bestLocation = getBestLocation(item);
      setPickedLocation(bestLocation);
      
      // Fetch current stock for this item using search endpoint
      if (item.ean) {
        axios.get(`${API}/inventory`, { params: { search: item.ean, limit: 1 } })
          .then(res => {
            const items = res.data.items || [];
            const product = items.find(p => p.ean === item.ean);
            const totalStock = product?.total_antal || 0;
            setCurrentStock(totalStock);
          })
          .catch(() => setCurrentStock(null));
      } else {
        setCurrentStock(null);
      }
    }
  }, [pickingList, currentIndex, transportFilter, selectedItemId, lockedOrderNumber]);

  const fetchPickingList = async () => {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await axios.get(`${API}/picking/picking-lists/${listId}`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' }
      });
      setPickingList(response.data);
      
      // Check if completed
      if (response.data.status === 'completed') {
        setShowCompletedDialog(true);
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching picking list:", error);
      toast.error("Kunde inte ladda plocklistan");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Refresh inventory data for items in the picking list (live updates)
  const refreshItemsInventoryData = useCallback(async () => {
    if (!pickingList || pickingList.status === 'completed') return;
    
    try {
      // Get all pending/partial items
      const itemsToCheck = pickingList.items.filter(item => 
        item.status === 'pending' || item.status === 'partial'
      );
      
      if (itemsToCheck.length === 0) return;
      
      // Collect both EANs and artikelnummers (for items missing EAN)
      const eans = itemsToCheck.map(i => i.ean).filter(Boolean);
      const artikelnummers = itemsToCheck
        .filter(i => !i.ean && i.artikelnummer)
        .map(i => i.artikelnummer);
      
      // Fetch updated inventory data for these items
      const response = await axios.post(`${API}/picking/picking-lists/${listId}/refresh-items`, {
        eans: eans,
        artikelnummers: artikelnummers
      });
      
      if (response.data.updated_count > 0) {
        // Refresh the full list to get updated data
        await fetchPickingList();
        toast.success(`${response.data.updated_count} produkt(er) uppdaterades`);
      }
    } catch (error) {
      // Silent fail - don't show error for background refresh
      console.log("Background refresh:", error.message);
    }
  }, [pickingList, listId]);

  // Polling for live updates (every 15 seconds)
  useEffect(() => {
    if (!listId || !pickingList || pickingList.status === 'completed') return;
    
    const pollInterval = setInterval(() => {
      refreshItemsInventoryData();
    }, 15000); // 15 seconds
    
    return () => clearInterval(pollInterval);
  }, [listId, pickingList, refreshItemsInventoryData]);

  // Refresh when page gains focus
  useEffect(() => {
    const handleFocus = () => {
      if (pickingList && pickingList.status !== 'completed') {
        refreshItemsInventoryData();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [pickingList, refreshItemsInventoryData]);

  const handlePick = async () => {
    if (!currentItem) return;
    
    // Use pickedLocation if it's a valid location for THIS item, otherwise use best location
    let locationToUse = pickedLocation.trim();
    
    // Validate that the picked location is actually a valid location for this item
    const validLocations = currentItem?.alla_lagerplatser 
      ? currentItem.alla_lagerplatser.split(',').map(l => l.trim()).filter(l => l && l !== '0')
      : [currentItem?.lagerplats].filter(Boolean);
    
    if (!locationToUse || !validLocations.includes(locationToUse)) {
      // Fall back to the best location for this item
      locationToUse = getBestLocation(currentItem);
    }
    
    if (!locationToUse) {
      toast.error("Ingen lagerplats angiven");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.put(
        `${API}/picking/picking-lists/${listId}/items/${currentItem.id}/pick`,
        { picked_location: locationToUse }
      );

      // Handle partial pick
      if (response.data.partial_pick) {
        toast.warning(
          `Delplock: ${response.data.qty_picked_now} st plockad. Återstår ${response.data.qty_remaining} st att plocka från annan plats.`,
          { duration: 6000 }
        );
      } else if (response.data.auto_cancelled_bookings && response.data.auto_cancelled_bookings.length > 0) {
        const orders = response.data.auto_cancelled_bookings.join(', ');
        toast.success(
          `Produkt plockad! Bokning för order ${orders} har avbokats automatiskt.`,
          { duration: 5000 }
        );
      } else if (response.data.inventory_updated) {
        toast.success("Produkt plockad - lagersaldo uppdaterat!");
      } else {
        toast.success("Produkt plockad!");
      }
      
      if (response.data.status === 'completed') {
        setShowCompletedDialog(true);
      }
      
      // Clear selected item to continue with normal order
      setSelectedItemId(null);
      // Capture which order this pick belongs to (use existing lock if set)
      const effectiveLock = lockedOrderNumber || currentItem?.ordernummer || null;
      if (effectiveLock && effectiveLock !== lockedOrderNumber) {
        setLockedOrderNumber(effectiveLock);
      }
      const fresh = await fetchPickingList();
      advanceOrderLockIfDone(fresh, effectiveLock);
      setPickedLocation("");
    } catch (error) {
      // Handle blocked pick
      const detail = error.response?.data?.detail;
      if (detail?.blocked) {
        // Show the blocking reason with longer duration for important messages
        toast.error(detail.message || "Plockning blockerad", { duration: 6000 });
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error("Kunde inte markera produkten som plockad");
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleMissing = async () => {
    if (!currentItem) return;

    setProcessing(true);
    try {
      const response = await axios.put(
        `${API}/picking/picking-lists/${listId}/items/${currentItem.id}/missing`
      );

      toast.warning("Produkt markerad som saknad");
      
      if (response.data.status === 'completed') {
        setShowCompletedDialog(true);
      }
      
      // Clear selected item to continue with normal order
      setSelectedItemId(null);
      const effectiveLock = lockedOrderNumber || currentItem?.ordernummer || null;
      if (effectiveLock && effectiveLock !== lockedOrderNumber) {
        setLockedOrderNumber(effectiveLock);
      }
      const fresh = await fetchPickingList();
      advanceOrderLockIfDone(fresh, effectiveLock);
    } catch (error) {
      toast.error("Kunde inte markera produkten som saknad");
    } finally {
      setProcessing(false);
    }
  };

  const handleSkip = async () => {
    if (!currentItem) return;
    
    setProcessing(true);
    try {
      // Mark item as skipped in backend
      await axios.put(`${API}/picking/picking-lists/${listId}/items/${currentItem.id}/skip`);
      
      toast.info("Order överhoppad");
      
      // Clear selected item and refresh list
      setSelectedItemId(null);
      const effectiveLock = lockedOrderNumber || currentItem?.ordernummer || null;
      if (effectiveLock && effectiveLock !== lockedOrderNumber) {
        setLockedOrderNumber(effectiveLock);
      }
      const fresh = await fetchPickingList();
      advanceOrderLockIfDone(fresh, effectiveLock);
    } catch (error) {
      console.error("Error skipping item:", error);
      toast.error("Kunde inte hoppa över produkten");
    } finally {
      setProcessing(false);
    }
  };

  // Handle completing the picking list manually
  const handleCompleteList = async () => {
    setProcessing(true);
    try {
      await axios.put(`${API}/picking/picking-lists/${listId}/complete`);
      toast.success("Plocklista färdigställd!");
      navigate('/picking');
    } catch (error) {
      console.error("Error completing list:", error);
      toast.error(error.response?.data?.detail || "Kunde inte färdigställa plocklistan");
      // Refresh so the UI reflects any remaining pending items
      fetchPickingList();
    } finally {
      setProcessing(false);
    }
  };

  // Handle selecting a specific item from the full list dialog
  const handleSelectItem = (itemId) => {
    setSelectedItemId(itemId);
    // Lock onto that item's order so we stay within it after the next pick
    const target = pickingList?.items?.find(i => i.id === itemId);
    if (target && target.ordernummer) {
      setLockedOrderNumber(target.ordernummer);
    }
    setShowFullList(false);
    toast.info("Produkt vald - plocka den och fortsätt sedan med listan");
  };

  // Undo a skipped item — single click, no dialog (no inventory/booking side effects)
  const handleUndoSkip = async (item, e) => {
    if (e) e.stopPropagation();
    if (!item || item.status !== 'skipped') return;
    setProcessing(true);
    try {
      await axios.post(`${API}/picking/picking-lists/${listId}/items/${item.id}/undo-skip`);
      toast.success(`${item.artikelnummer || item.ean} är redo att plockas igen`);
      // Lock onto this order so picker continues here
      if (item.ordernummer) setLockedOrderNumber(item.ordernummer);
      setSelectedItemId(item.id);
      await fetchPickingList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ångra överhoppningen");
    } finally {
      setProcessing(false);
    }
  };

  // Open the "Ångra plock" dialog for a picked item
  const openUndoDialog = (item, e) => {
    if (e) e.stopPropagation();
    setUndoTarget(item);
    // Default the location to where it was actually picked from (fallback to original lagerplats)
    setUndoLocation(item.picked_location || item.lagerplats || "");
    // Default to rebook if there was an auto-cancelled booking snapshot
    setUndoRebook(true);
  };

  const closeUndoDialog = () => {
    setUndoTarget(null);
    setUndoLocation("");
    setUndoRebook(true);
  };

  const handleUndoPick = async () => {
    if (!undoTarget) return;
    const loc = (undoLocation || "").trim();
    if (!loc) {
      toast.error("Ange en lagerplats att lägga tillbaka produkten på");
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/picking/picking-lists/${listId}/items/${undoTarget.id}/undo-pick`,
        { location: loc, rebook: undoRebook },
      );
      const body = response.data || {};
      if (body.had_cancelled_bookings && body.rebooked > 0) {
        toast.success(`Plock ångrat – ${body.qty_restored} st åter på ${body.location}, ${body.rebooked} bokning(ar) återställda`);
      } else if (body.had_cancelled_bookings && !undoRebook) {
        toast.success(`Plock ångrat – ${body.qty_restored} st åter på ${body.location} (bokning kvar avbokad)`);
      } else {
        toast.success(`Plock ångrat – ${body.qty_restored} st åter på ${body.location}`);
      }
      // Lock onto this order again so the picker stays here
      if (undoTarget.ordernummer) setLockedOrderNumber(undoTarget.ordernummer);
      setSelectedItemId(undoTarget.id);
      closeUndoDialog();
      await fetchPickingList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ångra plocket");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Laddar plocklista...</p>
      </div>
    );
  }

  if (!pickingList) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-destructive">Plocklista hittades inte</p>
      </div>
    );
  }

  // Filter items by transport if filter is active
  // Include both 'pending' and 'partial' items as items that need to be picked
  const allPendingItems = pickingList.items.filter(item => item.status === 'pending' || item.status === 'partial');
  const pendingItems = transportFilter === 'all' 
    ? allPendingItems 
    : allPendingItems.filter(item => item.transportor === transportFilter);
  
  // Sorted pending items (mirrors the picking list view order)
  const sortedPendingItems = [...pendingItems].sort(sortByOrderAndArt);

  // Pick selection priority:
  // 1. Explicit user selection from the list dialog
  // 2. First pending item in the currently locked order (stay-on-order behaviour)
  // 3. First sorted pending item overall (fallback during initial render)
  const selectedItem = selectedItemId 
    ? pendingItems.find(item => item.id === selectedItemId) 
    : null;
  const lockedItem = lockedOrderNumber
    ? sortedPendingItems.find(item => item.ordernummer === lockedOrderNumber)
    : null;
  const currentItem = selectedItem || lockedItem || sortedPendingItems[0];
  
  // Get carrier color theme
  const carrierTheme = currentItem?.transportor === 'Bring' 
    ? 'bring' 
    : currentItem?.transportor === 'DSV' 
      ? 'dsv' 
      : 'default';
  
  // Calculate progress based on filter
  const filteredItems = transportFilter === 'all' 
    ? pickingList.items 
    : pickingList.items.filter(item => item.transportor === transportFilter);
  const filteredTotal = filteredItems.length;
  const filteredPicked = filteredItems.filter(item => item.status === 'picked').length;
  const filteredMissing = filteredItems.filter(item => item.status === 'missing').length;
  const filteredSkipped = filteredItems.filter(item => item.status === 'skipped').length;
  const filteredPending = filteredItems.filter(item => item.status === 'pending').length;
  
  // Total steps excludes skipped items (they're removed from the flow)
  const totalSteps = filteredTotal - filteredSkipped;
  
  // Current step = picked + missing + 1 (where we are now)
  // But capped at totalSteps when all are done
  const processedCount = filteredPicked + filteredMissing;
  const currentStep = Math.min(processedCount + 1, totalSteps);
  
  // Progress is based on all items including skipped
  const progress = filteredTotal > 0 
    ? Math.round(((processedCount + filteredSkipped) / filteredTotal) * 100)
    : 0;

  // Count by transport
  const bringCount = pickingList.items.filter(item => item.transportor === 'Bring' && item.status === 'pending').length;
  const dsvCount = pickingList.items.filter(item => item.transportor === 'DSV' && item.status === 'pending').length;

  // Sort locations logically: indoor first (A-Z), then outdoor (Ute)
  const sortLocations = (locations) => {
    return locations.sort((a, b) => {
      const aIsUte = a.toLowerCase().startsWith('ute');
      const bIsUte = b.toLowerCase().startsWith('ute');
      
      // Indoor locations come before outdoor
      if (aIsUte && !bIsUte) return 1;
      if (!aIsUte && bIsUte) return -1;
      
      // Natural alphanumeric sort within same category
      const extractParts = (str) => {
        const match = str.match(/^([A-Za-z]+)(\d*)(.*)$/);
        if (match) {
          return [match[1].toUpperCase(), parseInt(match[2]) || 0, match[3]];
        }
        return [str, 0, ''];
      };
      
      const [aLetter, aNum, aRest] = extractParts(a);
      const [bLetter, bNum, bRest] = extractParts(b);
      
      if (aLetter !== bLetter) return aLetter.localeCompare(bLetter);
      if (aNum !== bNum) return aNum - bNum;
      return aRest.localeCompare(bRest);
    });
  };

  // Get available locations for current item
  const getAvailableLocations = () => {
    if (!currentItem?.alla_lagerplatser) return [];
    const locations = currentItem.alla_lagerplatser.split(',').map(loc => loc.trim()).filter(loc => loc && loc !== '0');
    return sortLocations(locations);
  };
  
  const availableLocations = getAvailableLocations();
  const hasMultipleLocations = availableLocations.length > 1;
  const hasNoLocation = !currentItem?.lagerplats && availableLocations.length === 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card shrink-0">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/picking">
                <Button variant="ghost" size="icon" className="shrink-0" data-testid="back-btn">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="min-w-0">
                <h1 className="font-heading text-base sm:text-xl font-bold uppercase tracking-tight truncate" data-testid="list-name">
                  {pickingList.name}
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Steg {currentStep} av {totalSteps}
                  {transportFilter !== 'all' && ` (${transportFilter})`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  refreshItemsInventoryData();
                  toast.info("Uppdaterar...");
                }}
                className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3 sm:gap-2"
                data-testid="refresh-btn"
                title="Uppdatera lagerdata"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowDetailsDialog(true)}
                  className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3 sm:gap-2"
                  data-testid="show-details-btn"
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">Detaljer</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowFullList(true)}
                className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3 sm:gap-2"
                data-testid="show-full-list-btn"
              >
                <List className="h-4 w-4" />
                <span className="hidden sm:inline">Visa lista</span>
              </Button>
            </div>
          </div>
          
          {/* Stats row - compact on mobile */}
          <div className="flex items-center gap-3 mt-3">
            <div 
              className="flex items-center gap-1 cursor-pointer hover:bg-muted/50 px-2 py-1 rounded-md transition-colors"
              onClick={() => { setStatusFilter('all'); setShowFilteredList(true); }}
              data-testid="stat-total"
            >
              <p className="text-xs text-muted-foreground">Totalt</p>
              <p className="font-bold text-sm">{totalSteps}</p>
            </div>
            <div 
              className="flex items-center gap-1 cursor-pointer hover:bg-success/10 px-2 py-1 rounded-md transition-colors"
              onClick={() => { setStatusFilter('picked'); setShowFilteredList(true); }}
              data-testid="stat-picked"
            >
              <p className="text-xs text-muted-foreground">Plockade</p>
              <p className="font-bold text-sm text-success">{filteredPicked}</p>
            </div>
            {filteredSkipped > 0 && (
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-amber-500/10 px-2 py-1 rounded-md transition-colors"
                onClick={() => { setStatusFilter('skipped'); setShowFilteredList(true); }}
                data-testid="stat-skipped"
              >
                <p className="text-xs text-muted-foreground">Overh.</p>
                <p className="font-bold text-sm text-amber-500">{filteredSkipped}</p>
              </div>
            )}
          </div>
          
          {/* Transport Filter Buttons */}
          {(bringCount > 0 || dsvCount > 0) && (
            <div className="flex items-center gap-2 mt-3">
              <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex items-center gap-1.5 overflow-x-auto">
                <Button
                  variant={transportFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs shrink-0"
                  onClick={() => setTransportFilter('all')}
                  data-testid="filter-all"
                >
                  Alla ({allPendingItems.length})
                </Button>
                {bringCount > 0 && (
                  <Button
                    variant={transportFilter === 'Bring' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-xs shrink-0 ${transportFilter === 'Bring' ? 'bg-green-600 hover:bg-green-700' : 'text-green-500 border-green-500/50'}`}
                    onClick={() => setTransportFilter('Bring')}
                    data-testid="filter-bring"
                  >
                    Bring ({bringCount})
                  </Button>
                )}
                {dsvCount > 0 && (
                  <Button
                    variant={transportFilter === 'DSV' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-xs shrink-0 ${transportFilter === 'DSV' ? 'bg-blue-600 hover:bg-blue-700' : 'text-blue-500 border-blue-500/50'}`}
                    onClick={() => setTransportFilter('DSV')}
                    data-testid="filter-dsv"
                  >
                    DSV ({dsvCount})
                  </Button>
                )}
                <div className="w-px h-5 bg-border shrink-0 ml-2 mr-1" />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs shrink-0 gap-1"
                  onClick={() => setShowProductSummary(true)}
                  data-testid="product-summary-btn"
                >
                  <Layers className="h-3.5 w-3.5" />
                  Summa produkter
                </Button>
              </div>
            </div>
          )}
          
          {/* Progress */}
          <div className="mt-3">
            <Progress value={progress} className="h-2 sm:h-3" data-testid="progress-bar" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-4 sm:py-6 flex flex-col">
        {currentItem ? (
          <div className="flex-1 flex flex-col animate-slide-in" data-testid="current-item">
            {/* Location - Big and prominent - Hide when item cannot be picked */}
            {!(currentItem.warning_type === 'no_ean' || currentItem.warning_type === 'not_in_system' || currentItem.warning_type === 'zero_stock') && (
            <Card className={`mb-4 bg-card ${
              carrierTheme === 'bring' ? 'border-green-500/40' : 
              carrierTheme === 'dsv' ? 'border-blue-500/40' : 
              'border-primary/30'
            }`}>
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <MapPin className={`h-6 w-6 ${
                    carrierTheme === 'bring' ? 'text-green-500' : 
                    carrierTheme === 'dsv' ? 'text-blue-500' : 
                    'text-primary'
                  }`} />
                  <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    {hasNoLocation ? 'Ange lagerplats' : hasMultipleLocations ? 'Välj lagerplats' : 'Lagerplats'}
                  </span>
                </div>
                
                {hasNoLocation ? (
                  <div className="max-w-md mx-auto">
                    <Input
                      value={pickedLocation}
                      onChange={(e) => setPickedLocation(e.target.value.toUpperCase())}
                      placeholder="Skriv lagerplats..."
                      className={`h-14 sm:h-20 text-2xl sm:text-3xl font-mono text-center placeholder:text-muted-foreground/50 ${
                        carrierTheme === 'bring' ? 'text-green-500 border-green-500/50' : 
                        carrierTheme === 'dsv' ? 'text-blue-500 border-blue-500/50' : 
                        'text-primary border-primary/50'
                      }`}
                      data-testid="location-input"
                    />
                  </div>
                ) : hasMultipleLocations ? (
                  <div className="max-w-md mx-auto">
                    <Select value={pickedLocation} onValueChange={setPickedLocation}>
                      <SelectTrigger className={`h-14 sm:h-20 text-2xl sm:text-3xl font-mono ${
                        carrierTheme === 'bring' ? 'text-green-500 border-green-500/50' : 
                        carrierTheme === 'dsv' ? 'text-blue-500 border-blue-500/50' : 
                        'text-primary border-primary/50'
                      }`} data-testid="location-select">
                        <SelectValue placeholder="Välj plats" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLocations.map((loc) => (
                          <SelectItem key={loc} value={loc} className="text-lg font-mono py-3">
                            {loc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-2">
                      {availableLocations.length} tillgängliga platser
                    </p>
                  </div>
                ) : (
                  <p className={`location-code ${
                    carrierTheme === 'bring' ? '!text-green-500' : 
                    carrierTheme === 'dsv' ? '!text-blue-500' : 
                    ''
                  }`} data-testid="item-location">
                    {currentItem.lagerplats || "—"}
                  </p>
                )}
              </CardContent>
            </Card>
            )}

            {/* Product Info */}
            <Card className={`mb-4 flex-1 ${
              carrierTheme === 'bring' ? 'border-green-500/20' : 
              carrierTheme === 'dsv' ? 'border-blue-500/20' : 
              ''
            }`}>
              <CardContent className="p-4 sm:p-6 space-y-3">
                {/* Artikelnummer - Large at top */}
                <div className="flex items-center gap-3">
                  <Barcode className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Artikelnummer</p>
                    <p className="font-mono text-xl sm:text-2xl font-bold truncate" data-testid="item-artikelnummer">{currentItem.artikelnummer}</p>
                  </div>
                </div>

                {/* EAN & Product */}
                <div className="pl-8 flex flex-col sm:flex-row gap-1 sm:gap-6">
                  {currentItem.ean && (
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">EAN</p>
                      <p className="font-mono text-sm text-muted-foreground truncate" data-testid="item-ean">{currentItem.ean}</p>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Produkt</p>
                    <p className="text-sm text-muted-foreground truncate" data-testid="item-name">{currentItem.produktnamn}</p>
                  </div>
                </div>

                {/* Quantity - Show total if grouped */}
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      {currentItem.status === 'partial' ? 'Återstår att plocka' : (currentItem.total_antal ? 'Totalt antal att plocka' : 'Antal')}
                    </p>
                    {currentItem.status === 'partial' ? (
                      <>
                        <p className="text-2xl font-bold text-amber-500" data-testid="item-quantity">
                          {(currentItem.total_antal || currentItem.antal) - (currentItem.picked_quantity || 0)} st
                        </p>
                        <p className="text-sm text-amber-400">
                          Redan plockat: {currentItem.picked_quantity || 0} av {currentItem.total_antal || currentItem.antal} st
                        </p>
                        {currentItem.pick_history && currentItem.pick_history.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Senast från: {currentItem.pick_history[currentItem.pick_history.length - 1]?.location}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold" data-testid="item-quantity">
                          {currentItem.total_antal || currentItem.antal} st
                        </p>
                        {currentItem.total_antal && currentItem.total_antal !== currentItem.antal && (
                          <p className="text-sm text-muted-foreground">
                            ({currentItem.antal} st per order)
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Orders - Show multiple if grouped */}
                <div className="pt-4 border-t border-border">
                  <div className="flex flex-wrap items-start gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        {currentItem.ordrar && currentItem.ordrar.length > 1 ? 'Ordrar' : 'Order'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {currentItem.ordrar && currentItem.ordrar.length > 0 ? (
                          currentItem.ordrar.map((order, idx) => (
                            <span key={idx} className="order-badge" data-testid={`item-order-${idx}`}>
                              {order}
                            </span>
                          ))
                        ) : (
                          <span className="order-badge" data-testid="item-order">
                            {currentItem.ordernummer}
                          </span>
                        )}
                      </div>
                      {currentItem.ordrar && currentItem.ordrar.length > 1 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Samma produkt till {currentItem.ordrar.length} olika ordrar
                        </p>
                      )}
                    </div>
                    {currentItem.transportor && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Transportör</p>
                        <span className={`order-badge ${currentItem.transportor === 'Bring' ? 'bg-green-900/50 text-green-400' : 'bg-blue-900/50 text-blue-400'}`} data-testid="item-transportor">
                          {currentItem.transportor}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comment */}
                {currentItem.kommentar && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Kommentar</p>
                    <p className="text-sm text-muted-foreground" data-testid="item-comment">
                      {currentItem.kommentar}
                    </p>
                  </div>
                )}

                {/* Warning for items with issues */}
                {currentItem.warning && (
                  <div className="pt-4 border-t border-border">
                    <div className={`flex items-start gap-3 p-3 rounded-lg ${
                      currentItem.warning_type === 'no_ean' 
                        ? 'bg-orange-500/10 border border-orange-500/30' 
                        : currentItem.warning_type === 'not_in_system'
                        ? 'bg-red-500/10 border border-red-500/30'
                        : 'bg-amber-500/10 border border-amber-500/30'
                    }`}>
                      <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                        currentItem.warning_type === 'no_ean' 
                          ? 'text-orange-500' 
                          : currentItem.warning_type === 'not_in_system'
                          ? 'text-red-500'
                          : 'text-amber-500'
                      }`} />
                      <div>
                        <p className={`font-medium ${
                          currentItem.warning_type === 'no_ean' 
                            ? 'text-orange-500' 
                            : currentItem.warning_type === 'not_in_system'
                            ? 'text-red-500'
                            : 'text-amber-500'
                        }`}>
                          {currentItem.warning}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Same order notification */}
                {currentItem.ordernummer && (() => {
                  const sameOrderItems = pickingList?.items?.filter(item => 
                    item.id !== currentItem.id && 
                    item.ordernummer === currentItem.ordernummer &&
                    item.status === 'pending'
                  ) || [];
                  
                  if (sameOrderItems.length > 0) {
                    return (
                      <div className="pt-4 border-t border-border">
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/30">
                          <AlertTriangle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-primary">
                              {sameOrderItems.length} fler produkt{sameOrderItems.length > 1 ? 'er' : ''} i samma order
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Order <span className="font-mono font-bold">{currentItem.ordernummer}</span> innehåller också:
                            </p>
                            <ul className="mt-2 space-y-1">
                              {sameOrderItems.slice(0, 5).map((item, idx) => (
                                <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                                  <span className="font-mono">{item.artikelnummer}</span>
                                  <span className="text-xs">({item.antal} st)</span>
                                </li>
                              ))}
                              {sameOrderItems.length > 5 && (
                                <li className="text-sm text-muted-foreground italic">
                                  + {sameOrderItems.length - 5} till...
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </CardContent>
            </Card>

            {/* Show selected location confirmation */}
            {pickedLocation && (
              <div className={`mb-4 p-3 rounded-md text-center ${
                carrierTheme === 'bring' ? 'bg-green-500/10 border border-green-500/30' : 
                carrierTheme === 'dsv' ? 'bg-blue-500/10 border border-blue-500/30' : 
                'bg-primary/10 border border-primary/30'
              }`}>
                <p className="text-sm text-muted-foreground">Plockas från</p>
                <p className={`text-2xl font-mono font-bold ${
                  carrierTheme === 'bring' ? 'text-green-500' : 
                  carrierTheme === 'dsv' ? 'text-blue-500' : 
                  'text-primary'
                }`}>{pickedLocation}</p>
              </div>
            )}

            {/* Show zero stock warning */}
            {currentStock === 0 && (
              <div className="mb-4 p-3 rounded-md text-center bg-red-500/10 border border-red-500/30">
                <p className="text-sm text-red-500 font-bold">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  Lagersaldo är 0 - Skanna in produkten först!
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3 pb-20 md:pb-4">
              <Button
                className={`big-button ${
                  (currentStock === 0 || currentItem?.warning_type === 'no_ean' || currentItem?.warning_type === 'not_in_system')
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : carrierTheme === 'bring' ? 'bg-green-600 hover:bg-green-700' : 
                      carrierTheme === 'dsv' ? 'bg-blue-600 hover:bg-blue-700' : 
                      'animate-pulse-glow'
                }`}
                onClick={handlePick}
                disabled={processing || currentStock === 0 || currentItem?.warning_type === 'no_ean' || currentItem?.warning_type === 'not_in_system'}
                data-testid="confirm-pick-btn"
              >
                <CheckCircle className="h-6 w-6 mr-2" />
                {currentItem?.warning_type === 'no_ean' 
                  ? 'Saknar EAN' 
                  : currentItem?.warning_type === 'not_in_system'
                  ? 'Ej i systemet'
                  : currentStock === 0 
                  ? 'Lagersaldo 0' 
                  : 'Bekräfta plock'}
              </Button>
              <Button
                variant="outline"
                className="big-button border-muted-foreground/30 hover:bg-muted"
                onClick={handleSkip}
                disabled={processing}
                data-testid="skip-btn"
              >
                <SkipForward className="h-6 w-6 mr-2" />
                Hoppa över
              </Button>
            </div>
          </div>
        ) : allPendingItems.length > 0 ? (
          /* Current filter is done, but other items still need picking — block finishing */
          <div className="flex-1 flex items-center justify-center">
            <Card className="p-8 text-center max-w-md" data-testid="filter-done-more-pending">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-amber-500" />
              <h2 className="font-heading text-2xl font-bold mb-2">
                {transportFilter === 'all' ? 'Inga fler i vyn' : `Klar med ${transportFilter}!`}
              </h2>
              <p className="text-muted-foreground mb-4">
                Det finns fortfarande <span className="font-bold text-amber-500">{allPendingItems.length}</span> produkt(er) kvar att plocka för andra transportörer. Plocklistan kan inte färdigställas än.
              </p>
              <div className="flex flex-col gap-3">
                {bringCount > 0 && (
                  <Button
                    className="w-full"
                    onClick={() => { setTransportFilter('Bring'); setSelectedItemId(null); setLockedOrderNumber(null); }}
                    data-testid="continue-bring-btn"
                  >
                    Fortsätt med Bring ({bringCount})
                  </Button>
                )}
                {dsvCount > 0 && (
                  <Button
                    className="w-full"
                    onClick={() => { setTransportFilter('DSV'); setSelectedItemId(null); setLockedOrderNumber(null); }}
                    data-testid="continue-dsv-btn"
                  >
                    Fortsätt med DSV ({dsvCount})
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { setTransportFilter('all'); setSelectedItemId(null); setLockedOrderNumber(null); }}
                  data-testid="show-all-remaining-btn"
                >
                  Visa alla kvarvarande ({allPendingItems.length})
                </Button>
                <Link to="/picking">
                  <Button variant="ghost" data-testid="back-to-picking-btn">Till plocklistor</Button>
                </Link>
              </div>
            </Card>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <Card className="p-8 text-center max-w-md" data-testid="all-picked">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-success" />
              <h2 className="font-heading text-2xl font-bold mb-2">Plocklista klar!</h2>
              <p className="text-muted-foreground mb-4">
                Alla produkter har bearbetats
              </p>
              
              <div className="flex flex-col gap-3">
                <Button 
                  className="w-full"
                  onClick={handleCompleteList}
                  disabled={processing}
                  data-testid="complete-list-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Färdigställ plocklista
                </Button>
                <div className="flex gap-4 justify-center">
                  <Link to="/picking">
                    <Button variant="outline" data-testid="back-to-picking-btn">
                      Till plocklistor
                    </Button>
                  </Link>
                  <Link to={`/history/${listId}`}>
                    <Button variant="ghost" data-testid="view-summary-btn">
                      Visa sammanfattning
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        )}
      </main>

      {/* Completed Dialog */}
      <Dialog open={showCompletedDialog} onOpenChange={setShowCompletedDialog}>
        <DialogContent data-testid="completed-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-success" />
              Plocklista slutförd!
            </DialogTitle>
            <DialogDescription>
              Alla produkter i denna plocklista har bearbetats.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="text-center p-4 bg-success/10 rounded-md">
              <p className="text-3xl font-bold text-success">{pickingList?.picked_count || 0}</p>
              <p className="text-sm text-muted-foreground">Plockade</p>
            </div>
            <div className="text-center p-4 bg-amber-500/10 rounded-md">
              <p className="text-3xl font-bold text-amber-500">{pickingList?.skipped_count || 0}</p>
              <p className="text-sm text-muted-foreground">Överhoppade</p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Link to="/picking">
              <Button variant="outline" data-testid="dialog-dashboard-btn">
                Till plocklistor
              </Button>
            </Link>
            <Link to="/inventory">
              <Button data-testid="dialog-summary-btn">
                Till lagret
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Undo Pick Dialog */}
      <Dialog open={!!undoTarget} onOpenChange={(open) => !open && closeUndoDialog()}>
        <DialogContent data-testid="undo-pick-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-amber-600" />
              Ångra plock
            </DialogTitle>
            <DialogDescription>
              {undoTarget?.artikelnummer} – {undoTarget?.picked_quantity || undoTarget?.antal || 0} st
              läggs tillbaka. Välj plats och bestäm om bokningen ska återställas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Lagerplats att lägga tillbaka på</Label>
              <Input
                value={undoLocation}
                onChange={(e) => setUndoLocation(e.target.value)}
                placeholder="t.ex. C12-PL6-N2"
                data-testid="undo-location-input"
                className="font-mono"
              />
              {undoTarget?.picked_location && undoTarget.picked_location !== undoLocation && (
                <button
                  type="button"
                  className="mt-2 text-xs text-primary hover:underline"
                  onClick={() => setUndoLocation(undoTarget.picked_location)}
                  data-testid="undo-use-picked-location"
                >
                  Använd samma plats som plocket: <span className="font-mono">{undoTarget.picked_location}</span>
                </button>
              )}
              {undoTarget?.alla_lagerplatser && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {undoTarget.alla_lagerplatser.split(',')
                    .map(l => l.trim())
                    .filter(l => l && l !== '0')
                    .map(l => (
                      <button
                        key={l}
                        type="button"
                        className={`px-2 py-1 rounded text-xs font-mono border transition-colors ${
                          undoLocation === l
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border hover:border-primary/60'
                        }`}
                        onClick={() => setUndoLocation(l)}
                        data-testid={`undo-quick-loc-${l}`}
                      >
                        {l}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {(undoTarget?.auto_cancelled_bookings && undoTarget.auto_cancelled_bookings.length > 0) ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">
                  Produkten var bokad – vad ska hända med bokningen?
                </p>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="undo-rebook"
                      checked={undoRebook}
                      onChange={() => setUndoRebook(true)}
                      data-testid="undo-rebook-yes"
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium">Boka produkten igen</div>
                      <div className="text-xs text-muted-foreground">
                        Återskapar {undoTarget.auto_cancelled_bookings.length} bokning(ar)
                        {undoTarget.auto_cancelled_bookings.map(b => b.customer_order_number || b.comment)
                          .filter(Boolean).length > 0 && (
                          <> för order {undoTarget.auto_cancelled_bookings
                            .map(b => b.customer_order_number || b.comment)
                            .filter(Boolean).join(', ')}</>
                        )}
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="undo-rebook"
                      checked={!undoRebook}
                      onChange={() => setUndoRebook(false)}
                      data-testid="undo-rebook-no"
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium">Boka inte</div>
                      <div className="text-xs text-muted-foreground">
                        Produkten läggs tillbaka men förblir avbokad
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Ingen bokning behöver återställas för denna produkt.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeUndoDialog} disabled={processing} data-testid="undo-cancel-btn">
              Avbryt
            </Button>
            <Button
              onClick={handleUndoPick}
              disabled={processing || !undoLocation.trim()}
              data-testid="undo-confirm-btn"
            >
              {processing ? "Ångrar..." : "Ångra plock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full List Dialog */}
      <Dialog open={showFullList} onOpenChange={setShowFullList}>
        <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="full-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <List className="h-5 w-5" />
              Hela plocklistan
            </DialogTitle>
            <DialogDescription>
              {filteredTotal} produkter totalt • {filteredPicked} plockade • {filteredMissing} saknade • {pendingItems.length} kvar
              {pendingItems.length > 0 && <span className="block mt-1 text-primary">Klicka på en produkt för att plocka den</span>}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              {(() => {
                // Sort items by order number to group same orders together
                const sortedItems = [...filteredItems].sort((a, b) => {
                  const orderA = a.ordernummer || '';
                  const orderB = b.ordernummer || '';
                  if (orderA !== orderB) return orderA.localeCompare(orderB);
                  return (a.artikelnummer || '').localeCompare(b.artikelnummer || '');
                });
                
                // Group items by order number
                const orderGroups = {};
                const singleItems = [];
                
                sortedItems.forEach(item => {
                  const order = item.ordernummer;
                  if (order) {
                    if (!orderGroups[order]) {
                      orderGroups[order] = [];
                    }
                    orderGroups[order].push(item);
                  } else {
                    singleItems.push(item);
                  }
                });
                
                // Separate multi-item orders from single-item orders
                const multiItemOrders = Object.entries(orderGroups).filter(([_, items]) => items.length > 1);
                const singleItemOrders = Object.entries(orderGroups).filter(([_, items]) => items.length === 1);
                
                // Render function for individual item
                const renderItem = (item, inGroup = false) => (
                  <div
                    key={item.id}
                    onClick={() => (item.status === 'pending' || item.status === 'partial') && handleSelectItem(item.id)}
                    className={`p-3 rounded-md border transition-all ${
                      item.status === 'picked' ? 'bg-success/10 border-success/40' :
                      item.status === 'partial' ? 'bg-amber-500/10 border-amber-500/40' :
                      item.status === 'skipped' ? 'bg-amber-500/10 border-amber-500/40' :
                      'bg-card border-border hover:border-primary hover:bg-primary/5 cursor-pointer'
                    }`}
                    data-testid={`list-item-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {item.status === 'picked' && <CheckCircle className="h-4 w-4 text-success" />}
                          {item.status === 'partial' && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                          {item.status === 'skipped' && <SkipForward className="h-4 w-4 text-amber-500" />}
                          {item.status === 'pending' && <Package className="h-4 w-4 text-muted-foreground" />}
                          <span className="font-medium">{item.produktnamn}</span>
                          {!inGroup && item.ordernummer && (
                            <Badge variant="outline" className="ml-2 font-mono text-xs">
                              Ref: {item.ordernummer}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                          <span className="font-mono">{item.artikelnummer}</span>
                          {item.ean && <span className="font-mono text-xs">EAN: {item.ean}</span>}
                          {item.status === 'partial' ? (
                            <span className="font-bold text-amber-500">{item.picked_quantity || 0}/{item.total_antal || item.antal} st plockade</span>
                          ) : (
                            <span>{item.total_antal || item.antal} st</span>
                          )}
                          <span className="font-mono">{item.lagerplats || '—'}</span>
                          {item.transportor && (
                            <Badge variant="outline" className={item.transportor === 'Bring' ? 'text-green-500' : 'text-blue-500'}>
                              {item.transportor}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {(item.status === 'pending' || item.status === 'partial') ? (
                        <Button variant="ghost" size="sm" className={item.status === 'partial' ? "text-amber-500 hover:text-amber-600" : "text-primary hover:text-primary"}>
                          {item.status === 'partial' ? 'Fortsätt plocka' : 'Plocka'}
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant={item.status === 'picked' ? 'default' : 'secondary'} className={item.status === 'skipped' ? 'bg-amber-500/20 text-amber-500' : ''}>
                            {item.status === 'picked' ? 'Plockad' : item.status === 'skipped' ? 'Överhoppad' : 'Saknad'}
                          </Badge>
                          {item.status === 'picked' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                              onClick={(e) => openUndoDialog(item, e)}
                              data-testid={`undo-pick-${item.id}`}
                            >
                              <Undo2 className="h-3.5 w-3.5 mr-1" />
                              Ångra plock
                            </Button>
                          )}
                          {item.status === 'skipped' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                              onClick={(e) => handleUndoSkip(item, e)}
                              disabled={processing}
                              data-testid={`undo-skip-${item.id}`}
                            >
                              <Undo2 className="h-3.5 w-3.5 mr-1" />
                              Ångra
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
                
                return (
                  <>
                    {/* Multi-item order groups with distinct background */}
                    {multiItemOrders.map(([orderNum, items]) => (
                      <div key={`group-${orderNum}`} className="rounded-lg bg-primary/10 border-2 border-primary/30 p-3">
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-primary/20">
                          <span className="text-sm font-bold text-primary">
                            📦 Order {orderNum}
                          </span>
                          <Badge className="bg-primary/20 text-primary border-0">
                            {items.length} produkter
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {items.map(item => renderItem(item, true))}
                        </div>
                      </div>
                    ))}
                    
                    {/* Single-item orders (with order number) */}
                    {singleItemOrders.map(([_, items]) => renderItem(items[0], false))}
                    
                    {/* Items without order number */}
                    {singleItems.map(item => renderItem(item, false))}
                  </>
                );
              })()}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Filtered Status List Dialog */}
      <Dialog open={showFilteredList} onOpenChange={setShowFilteredList}>
        <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="filtered-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              {statusFilter === 'all' && <><Package className="h-5 w-5" /> Alla produkter</>}
              {statusFilter === 'picked' && <><CheckCircle className="h-5 w-5 text-success" /> Plockade produkter</>}
              {statusFilter === 'missing' && <><AlertTriangle className="h-5 w-5 text-destructive" /> Saknade produkter</>}
              {statusFilter === 'pending' && <><Package className="h-5 w-5 text-primary" /> Kvarvarande produkter</>}
            </DialogTitle>
            <DialogDescription>
              {statusFilter === 'all' && `${filteredTotal} produkter totalt`}
              {statusFilter === 'picked' && `${filteredPicked} produkter plockade`}
              {statusFilter === 'missing' && `${filteredMissing} produkter saknade`}
              {statusFilter === 'pending' && `${pendingItems.length} produkter kvar att plocka`}
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter tabs */}
          <div className="flex items-center gap-2 border-b pb-3">
            <Button 
              variant={statusFilter === 'all' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter('all')}
            >
              Alla ({filteredTotal})
            </Button>
            <Button 
              variant={statusFilter === 'pending' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter('pending')}
            >
              Kvar ({pendingItems.length})
            </Button>
            <Button 
              variant={statusFilter === 'picked' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter('picked')}
              className={statusFilter === 'picked' ? 'bg-success hover:bg-success/90' : 'text-success border-success/50'}
            >
              Plockade ({filteredPicked})
            </Button>
          </div>
          
          <ScrollArea className="max-h-[55vh] pr-4">
            <div className="space-y-2">
              {filteredItems
                .filter(item => {
                  if (statusFilter === 'all') return true;
                  return item.status === statusFilter;
                })
                .map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    if (item.status === 'pending' || item.status === 'partial') {
                      handleSelectItem(item.id);
                      setShowFilteredList(false);
                    }
                  }}
                  className={`p-3 rounded-md border transition-all ${
                    item.status === 'picked' ? 'bg-success/5 border-success/30' :
                    item.status === 'partial' ? 'bg-amber-500/10 border-amber-500/40' :
                    item.status === 'skipped' ? 'bg-amber-500/5 border-amber-500/30' :
                    'bg-card border-border hover:border-primary hover:bg-primary/5 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {item.status === 'picked' && <CheckCircle className="h-4 w-4 text-success" />}
                        {item.status === 'partial' && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                        {item.status === 'skipped' && <SkipForward className="h-4 w-4 text-amber-500" />}
                        {item.status === 'pending' && <Package className="h-4 w-4 text-muted-foreground" />}
                        <span className="font-medium">{item.produktnamn}</span>
                        {item.ordernummer && (
                          <Badge variant="outline" className="ml-2 font-mono text-xs">
                            Ref: {item.ordernummer}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                        <span className="font-mono">{item.artikelnummer}</span>
                        {item.ean && <span className="font-mono text-xs">EAN: {item.ean}</span>}
                        {item.status === 'partial' ? (
                          <span className="font-bold text-amber-500">{item.picked_quantity || 0}/{item.total_antal || item.antal} st plockade</span>
                        ) : (
                          <span>{item.total_antal || item.antal} st</span>
                        )}
                        <span className="font-mono">{item.lagerplats || '—'}</span>
                        {item.transportor && (
                          <Badge variant="outline" className={item.transportor === 'Bring' ? 'text-green-500' : 'text-blue-500'}>
                            {item.transportor}
                          </Badge>
                        )}
                        {item.status === 'picked' && item.picked_location && (
                          <span className="text-success">Plockad från: {item.picked_location}</span>
                        )}
                        {item.status === 'partial' && item.pick_history && item.pick_history.length > 0 && (
                          <span className="text-amber-500">Senast plockad från: {item.pick_history[item.pick_history.length - 1]?.location}</span>
                        )}
                      </div>
                    </div>
                    {(item.status === 'pending' || item.status === 'partial') ? (
                      <Button variant="ghost" size="sm" className={item.status === 'partial' ? "text-amber-500 hover:text-amber-600" : "text-primary hover:text-primary"}>
                        {item.status === 'partial' ? 'Fortsätt' : 'Plocka'}
                      </Button>
                    ) : (
                      <Badge variant={item.status === 'picked' ? 'default' : 'secondary'} className={item.status === 'skipped' ? 'bg-amber-500/20 text-amber-500' : ''}>
                        {item.status === 'picked' ? 'Plockad' : item.status === 'skipped' ? 'Överhoppad' : 'Saknad'}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
              {filteredItems.filter(item => statusFilter === 'all' ? true : item.status === statusFilter).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Inga produkter med denna status
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Details Dialog - Admin only */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Plockdetaljer - {pickingList?.name}
            </DialogTitle>
            <DialogDescription>
              Översikt över alla ordrar med referensnummer och status
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[55vh] pr-4">
            <div className="space-y-2">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold">{pickingList?.items?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Totalt</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-success">{pickingList?.picked_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Plockade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-500">{pickingList?.skipped_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Överhoppade</p>
                </div>
              </div>

              {/* Items table */}
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-left font-medium">Referensnummer</th>
                    <th className="p-2 text-left font-medium">Artikelnummer</th>
                    <th className="p-2 text-left font-medium">Produkt</th>
                    <th className="p-2 text-center font-medium">Antal</th>
                    <th className="p-2 text-left font-medium">Transportör</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pickingList?.items?.map((item) => (
                    <tr key={item.id} className={
                      item.status === 'picked' ? 'bg-success/5' :
                      item.status === 'partial' ? 'bg-amber-500/10' :
                      item.status === 'skipped' ? 'bg-amber-500/5' :
                      ''
                    }>
                      <td className="p-2">
                        {item.status === 'picked' && (
                          <Badge className="bg-success text-white">Plockad</Badge>
                        )}
                        {item.status === 'partial' && (
                          <Badge className="bg-amber-500 text-white">Delvis {item.picked_quantity || 0}/{item.total_antal || item.antal}</Badge>
                        )}
                        {item.status === 'skipped' && (
                          <Badge className="bg-amber-500 text-white">Överhoppad</Badge>
                        )}
                        {item.status === 'pending' && (
                          <Badge variant="outline">Väntar</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <span className="font-mono font-bold text-primary">
                          {item.ordernummer || '—'}
                        </span>
                      </td>
                      <td className="p-2 font-mono">{item.artikelnummer}</td>
                      <td className="p-2">{item.produktnamn}</td>
                      <td className="p-2 text-center">
                        {item.status === 'partial' ? (
                          <span className="text-amber-500 font-bold">{item.picked_quantity || 0}/{item.total_antal || item.antal} st</span>
                        ) : (
                          <span>{item.antal || 1} st</span>
                        )}
                      </td>
                      <td className="p-2">
                        {item.transportor && (
                          <Badge variant="outline" className={
                            item.transportor === 'Bring' ? 'text-green-500 border-green-500/50' :
                            item.transportor === 'DSV' ? 'text-blue-500 border-blue-500/50' :
                            ''
                          }>
                            {item.transportor}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Stäng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Summary Dialog */}
      <Dialog open={showProductSummary} onOpenChange={setShowProductSummary}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Summa produkter
            </DialogTitle>
            <DialogDescription>
              Unika produkter att plocka, sorterat efter antal
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[55vh]">
            <div className="space-y-1.5 pr-3">
              {(() => {
                // Group pending items by artikelnummer/EAN and sum quantities
                const grouped = {};
                const itemsToCount = transportFilter === 'all' 
                  ? allPendingItems 
                  : allPendingItems.filter(item => item.transportor === transportFilter);
                
                for (const item of itemsToCount) {
                  const key = item.ean || item.artikelnummer;
                  if (!grouped[key]) {
                    grouped[key] = {
                      artikelnummer: item.artikelnummer,
                      ean: item.ean,
                      produktnamn: item.produktnamn,
                      totalQty: 0,
                      orderCount: 0,
                    };
                  }
                  grouped[key].totalQty += (item.total_antal || item.antal || 1);
                  grouped[key].orderCount += 1;
                }
                
                const sorted = Object.values(grouped).sort((a, b) => b.totalQty - a.totalQty);
                const totalProducts = sorted.reduce((s, p) => s + p.totalQty, 0);
                
                return (
                  <>
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-3 px-1">
                      <span>{sorted.length} unika produkter</span>
                      <span className="font-bold text-foreground">{totalProducts} st totalt</span>
                    </div>
                    {sorted.map((p, i) => (
                      <div key={p.ean || i} className="flex items-center gap-3 p-2.5 rounded-md bg-muted/30 hover:bg-muted/50" data-testid={`summary-product-${i}`}>
                        <span className="text-lg font-bold w-12 text-right shrink-0 text-primary">{p.totalQty}</span>
                        <div className="min-w-0 flex-1">
                          <p className="font-mono font-medium text-sm truncate">{p.artikelnummer}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.produktnamn}</p>
                        </div>
                        {p.orderCount > 1 && (
                          <Badge variant="outline" className="shrink-0 text-xs">{p.orderCount} ordrar</Badge>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductSummary(false)}>Stang</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
